import { navigationModules } from '@/lib/navigation'
import type { Me } from './types'

/**
 * Pick the best post-login landing route for this principal.
 *
 * The previous behaviour hard-coded `/alerts` as the fallback, but
 * not every role holds `alerts:read` — a senior analyst with
 * `cases:read` but no alerts permission would log in and land on
 * a 403, with the only escape being to manually type a different
 * URL. This helper walks a preference ladder and returns the first
 * route the user actually has permission to view.
 *
 * Order rationale:
 *   1. /alerts          — the analyst hot-path; most operators want this
 *   2. /cases           — fallback for analysts without alerts:read
 *   3. /dashboard       — exec / compliance audience
 *   4. /portfolio       — exec entry when /dashboard is gated tighter
 *   5. /customers       — last resort for read-only customer roles
 *   6. /transactions    — for api_user-style roles
 *   7. /settings        — every authenticated user can open this; the
 *                         ultimate "no other surface fits" landing
 *
 * Super-admin (system:admin) always lands on /alerts because they
 * implicitly satisfy every gate; the first route in the ladder
 * wins.
 *
 * PLATFORM MODE (ADR-0024): every rule below points at a scope:'tenant'
 * surface (navigation.ts SCOPE MODEL). A platform admin with no tenant
 * selected (`is_platform_admin: true`, no tenant-view) has no tenant
 * context — landing them on /inbox et al. either 403s at the backend or,
 * for surfaces like alerts that authorise a cross-tenant fan-out for
 * platform scope (internal/api/alerts_handler.go AllTenants=true), renders
 * an unfiltered multi-tenant queue inside a UI built for one tenant. The
 * `system:admin` short-circuit in `holdsAny` (and the offline mock's broad
 * default grant set) would otherwise walk a platform-mode admin straight
 * into the ladder anyway, so this is a guard BEFORE the ladder, not a rule
 * inside it. See `pickLandingRoute`'s `isPlatformMode` parameter.
 */

interface LandingRule {
  to: string
  // The permissions required to land here. Match `useCanAny`
  // semantics: any of these in `me.permissions` is enough.
  any: readonly string[]
}

const LANDING_LADDER: readonly LandingRule[] = [
  // Inbox is the canonical analyst workspace — unified view of
  // every actionable item. Lands first whenever the user holds
  // inbox:read.
  { to: '/inbox',        any: ['inbox:read'] },
  { to: '/alerts',       any: ['alerts:read'] },
  { to: '/cases',        any: ['cases:read'] },
  // Compliance officers don't carry alerts/cases write but do hold
  // audit:read + sars:read; route them to the Compliance Dashboard
  // by preference instead of the generic /dashboard.
  { to: '/compliance',   any: ['sars:read'] },
  { to: '/dashboard',    any: ['audit:read'] },
  { to: '/portfolio',    any: ['audit:read'] },
  { to: '/customers',    any: ['customers:read'] },
  { to: '/transactions', any: ['transactions:read'] },
  { to: '/settings',     any: [] }, // sentinel — always matches
]

/**
 * RoutePermission maps a path prefix to the permissions required to
 * access it. Used by `validateNext` to reject a `?next=` deep-link
 * the user can't actually open. Keep in sync with the
 * RequirePermissionRoute guards in router.tsx — pages not listed
 * here are assumed open to any authenticated user (e.g. /settings).
 */
const PATH_GUARDS: ReadonlyArray<{ prefix: string; any: readonly string[] }> = [
  { prefix: '/inbox',                    any: ['inbox:read'] },
  { prefix: '/alerts',                   any: ['alerts:read'] },
  { prefix: '/cases',                    any: ['cases:read'] },
  { prefix: '/compliance',               any: ['audit:read'] },
  { prefix: '/my-dashboard',             any: ['alerts:read'] },
  { prefix: '/investigations',           any: ['cases:read', 'customers:read'] },
  { prefix: '/intelligence/consortium',  any: ['consortium:read'] },
  { prefix: '/customers',                any: ['customers:read'] },
  { prefix: '/transactions',             any: ['transactions:read'] },
  { prefix: '/blocks/approvals',         any: ['blocks:approve'] },
  { prefix: '/blocks/new',               any: ['blocks:request'] },
  { prefix: '/blocks',                   any: ['blocks:read'] },
  { prefix: '/sars',                     any: ['sars:read'] },
  { prefix: '/reports',                  any: ['reports:read'] },
  { prefix: '/handovers',                any: ['handovers:read'] },
  { prefix: '/workflow',                 any: ['workflow:read'] },
  { prefix: '/ml',                       any: ['ml:read'] },
  { prefix: '/dashboard',                any: ['audit:read'] },
  { prefix: '/portfolio',                any: ['audit:read'] },
  { prefix: '/role-dashboards',          any: ['audit:read'] },
  { prefix: '/highlights',               any: ['audit:read'] },
  { prefix: '/operations',               any: ['audit:read'] },
  { prefix: '/analytics',                any: ['audit:read'] },
  // Audit / ops surfaces — split per-page since migration 0102.
  // Mirrors the route guards in router.tsx.
  { prefix: '/admin/audit',              any: ['audit_log:read'] },
  { prefix: '/admin/access-log',         any: ['access_log:read'] },
  { prefix: '/admin/ingestion',          any: ['system:read'] },
  { prefix: '/admin/system-status',      any: ['system:read'] },
  { prefix: '/admin/batch',              any: ['system:read'] },
  { prefix: '/admin/demo-data',          any: ['demo:admin', 'system:admin'] },
  { prefix: '/admin',                    any: ['system:admin'] }, // fallback for /admin/*
]

const SUPER_ADMIN = 'system:admin'

function holdsAny(me: Me | undefined, any: readonly string[]): boolean {
  if (!me) return false
  if (me.permissions.includes(SUPER_ADMIN)) return true
  if (any.length === 0) return true
  return any.some((p) => me.permissions.includes(p))
}

/**
 * Looks up the ADR-0024 SCOPE MODEL classification for a path by walking
 * `navigationModules` (navigation.ts — the single source of truth for
 * scope, already consulted by `firstVisibleItemPath`/`canSeeNavItem`).
 * Deliberately reused rather than re-authoring a second hand-maintained
 * scope table in this file: `PATH_GUARDS` below already has a documented
 * drift problem (see the /alerts vs cases:read comment in router.tsx), and
 * a third table would make that worse, not better.
 *
 * Matches by prefix (`pathname === item.path` or a `/`-bounded prefix),
 * same longest-match-wins semantics as `PATH_GUARDS` below — NOT
 * `item.exact`, which navigation.ts uses for sidebar *highlighting*
 * precision (e.g. keeping /admin/challenges from lighting up on
 * /admin/challenges/settings). For a landing decision we want the
 * broader "is this generally a tenant surface" answer, so a request for
 * /blocks/approvals should still inherit the /blocks item's scope.
 *
 * Returns `undefined` for paths navigation.ts doesn't classify (e.g.
 * /settings, /investigations, /developers/onboarding) — those are treated
 * as open to every scope, matching the existing PATH_GUARDS fallback.
 */
function scopeForPath(pathname: string): 'platform' | 'tenant' | 'both' | undefined {
  let best: { path: string; scope: 'platform' | 'tenant' | 'both' } | undefined
  for (const mod of navigationModules) {
    for (const item of mod.items) {
      if (pathname !== item.path && !pathname.startsWith(item.path + '/')) continue
      const scope = item.scope ?? (item.platformAdminOnly ? 'platform' : 'both')
      if (!best || item.path.length > best.path.length) {
        best = { path: item.path, scope }
      }
    }
  }
  return best?.scope
}

export interface LandingOptions {
  /**
   * Pass `true` only when the principal is a platform admin with NO tenant
   * selected — i.e. `me.is_platform_admin === true && tenantViewId == null`.
   * Mirrors `MyDashboard.tsx`'s `isPlatformMode` predicate exactly; do not
   * invent a second shape for this check.
   *
   * At the login-form call site (routes.tsx) `selectedTenantId` is ALWAYS
   * null — it is session-only `useState` seeded fresh on every page load
   * (PlatformTenantProvider, features/platform/context.tsx) — so at the
   * moment of login `is_platform_admin` alone determines platform mode.
   * There is no tenant-view state to read yet; this is intentional, not an
   * oversight. The index-route landing (router.tsx) DOES read the live
   * tenant-view context, because that entry point can be hit after a
   * platform admin has already switched into a tenant view.
   */
  isPlatformMode?: boolean
}

/**
 * pickLandingRoute returns the best route for this `me`. Always
 * returns a string (worst case `/settings`).
 */
export function pickLandingRoute(me: Me | undefined, opts: LandingOptions = {}): string {
  if (opts.isPlatformMode) {
    // /my-dashboard (MyDashboard.tsx) already special-cases platform mode
    // and renders <ExecutiveDashboardPage/> — the correct, tenant-context-free
    // home for this persona. Every ladder rule below is scope:'tenant', so we
    // skip the ladder entirely rather than let holdsAny's system:admin
    // short-circuit walk this persona into a tenant surface.
    return '/my-dashboard'
  }
  for (const rule of LANDING_LADDER) {
    if (holdsAny(me, rule.any)) return rule.to
  }
  // Defensive fallback — shouldn't reach here because the sentinel
  // /settings rule has any=[] which always matches when me is set.
  return '/settings'
}

/**
 * validateNext checks whether the supplied deep-link is allowed for
 * the current `me`. Returns the path itself if it's safe to navigate
 * to, or null when the principal lacks the permission and we should
 * fall back to pickLandingRoute. Rejects external/protocol-relative
 * URLs as well, so a `?next=//evil.com` query can't redirect off-site.
 *
 * When `opts.isPlatformMode` is set (see `LandingOptions`), a
 * scope:'tenant' `next` path is rejected outright — a platform admin with
 * no tenant selected must not be deep-linked into a tenant surface, even
 * via a permission they happen to hold directly (the `system:admin`
 * short-circuit in `holdsAny` — and the offline mock's broad default grant
 * set, which includes `inbox:read` directly — would otherwise let
 * `?next=/inbox` straight through).
 */
export function validateNext(
  next: string | null,
  me: Me | undefined,
  opts: LandingOptions = {},
): string | null {
  if (!next) return null
  // Reject anything that isn't an in-app path.
  if (!next.startsWith('/') || next.startsWith('//')) return null
  // Strip query/hash for guard matching but preserve them in the return.
  const pathOnly = next.split(/[?#]/, 1)[0] ?? next
  if (opts.isPlatformMode && scopeForPath(pathOnly) === 'tenant') return null
  // Find the most-specific (longest matching prefix) guard.
  let match: { prefix: string; any: readonly string[] } | null = null
  for (const g of PATH_GUARDS) {
    if (pathOnly === g.prefix || pathOnly.startsWith(g.prefix + '/')) {
      if (!match || g.prefix.length > match.prefix.length) match = g
    }
  }
  // Unguarded path (e.g. /settings) — always safe.
  if (!match) return next
  // Permission-gated path — only return when the user holds it.
  if (holdsAny(me, match.any)) return next
  return null
}
